import { getApiErrorMessage } from '../../../services/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, X, Eye, EyeOff, RefreshCw, Settings, Users, Edit } from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
function apiWithToken(token) { const i = axios.create({ baseURL: BACKEND }); i.interceptors.request.use(c => { c.headers.Authorization = 'Bearer ' + token; return c; }); return i; }

export default function TenantUsersPanel({ tenantId, token }) {
  const api = apiWithToken(token);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState({});
  const [newPw, setNewPw] = useState({});
  const [showPw, setShowPw] = useState({});
  const [tuSort, handleTuSort] = useSortState('user_id', 'asc');

  useEffect(() => {
    api.get(`/api/eyvo-admin/tenants/${tenantId}/users`)
      .then(r => setUsers(r.data)).catch(err => { if (err?.response?.status >= 500) toast.error('Failed to load users'); })
      .finally(() => setLoading(false));
  }, [tenantId]); // eslint-disable-line

  const resetPassword = async (userId) => {
    const pw = newPw[userId]?.trim();
    if (!pw) { toast.error('Enter a password'); return; }
    if (pw.length < 8 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw) || !/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pw)) {
      toast.error('Password must have 8+ chars, uppercase, lowercase, number, and special character');
      return;
    }
    setResetting(p => ({ ...p, [userId]: true }));
    try {
      await api.post(`/api/eyvo-admin/tenants/${tenantId}/users/${userId}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${userId}`);
      setNewPw(p => ({ ...p, [userId]: '' }));
    } catch (e) { toast.error(getApiErrorMessage(e, 'Reset failed')); }
    setResetting(p => ({ ...p, [userId]: false }));
  };

  const autoReset = async (userId) => {
    const generated = Math.random().toString(36).slice(-10) + 'A1!';
    setResetting(p => ({ ...p, [userId]: true }));
    try {
      await api.post(`/api/eyvo-admin/tenants/${tenantId}/users/${userId}/reset-password`, { new_password: generated });
      toast.success(`Password reset to: ${generated}`, { duration: 8000, description: 'Copy this — it will not be shown again.' });
    } catch (e) { toast.error(getApiErrorMessage(e, 'Reset failed')); }
    setResetting(p => ({ ...p, [userId]: false }));
  };

  const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric', timeZone:'America/Los_Angeles'}); } catch { return ''; } };

  if (loading) return <p className="text-sm text-muted-foreground py-4 text-center">Loading users…</p>;
  if (!users.length) return <p className="text-sm text-muted-foreground py-4 text-center">No users registered yet.</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 border-b">
            <tr>
              <SortableTableHeader label="User ID" field="user_id" sortState={tuSort} onSort={handleTuSort} />
              <SortableTableHeader label="Name" field="first_name" sortState={tuSort} onSort={handleTuSort} />
              <SortableTableHeader label="Email" field="email" sortState={tuSort} onSort={handleTuSort} />
              <SortableTableHeader label="Type" field="user_type" sortState={tuSort} onSort={handleTuSort} />
              <SortableTableHeader label="Licensed" field="is_licensed" sortState={tuSort} onSort={handleTuSort} />
              <SortableTableHeader label="Last Login" field="last_login" sortState={tuSort} onSort={handleTuSort} />
              <th className="text-left px-3 py-2">Reset Password</th>
            </tr>
          </thead>
          <tbody>
            {sortData(users, tuSort.field, tuSort.dir).map(u => (
              <tr key={u.user_id} className="border-b last:border-0 hover:bg-accent/20">
                <td className="px-3 py-2 font-mono text-primary">{u.user_id}</td>
                <td className="px-3 py-2">{u.first_name} {u.last_name}</td>
                <td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2 capitalize">{u.user_type}</td>
                <td className="px-3 py-2">
                  <span className={u.is_licensed ? 'text-green-600 font-medium' : 'text-muted-foreground'}
                  >{u.is_licensed ? 'Yes' : 'No'}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{fmtDate(u.last_login)}</td>
                <td className="px-3 py-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <div className="relative">
                        <Input
                          type={showPw[u.user_id] ? 'text' : 'password'}
                          value={newPw[u.user_id] || ''}
                          onChange={e => setNewPw(p => ({ ...p, [u.user_id]: e.target.value }))}
                          placeholder="New password"
                          className="h-7 text-xs w-36 pr-7"
                        />
                        <button onClick={() => setShowPw(p => ({ ...p, [u.user_id]: !p[u.user_id] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                          aria-label="Toggle visibility">
                          {showPw[u.user_id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      </div>
                      <button onClick={() => resetPassword(u.user_id)} disabled={resetting[u.user_id]}
                        className="h-7 px-2 rounded-md bg-primary text-white text-xs font-medium hover:bg-primary/90 disabled:opacity-50">
                        Set
                      </button>
                      <button onClick={() => autoReset(u.user_id)} disabled={resetting[u.user_id]}
                        title="Generate a random password"
                        className="h-7 w-7 flex items-center justify-center rounded-md border border-input hover:bg-accent disabled:opacity-50">
                        <RefreshCw className={`h-3 w-3 ${resetting[u.user_id] ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                    {newPw[u.user_id] && (() => {
                      const pw = newPw[u.user_id];
                      const checks = [
                        { ok: pw.length >= 8, l: '8+ chars' },
                        { ok: /[A-Z]/.test(pw), l: 'A-Z' },
                        { ok: /[a-z]/.test(pw), l: 'a-z' },
                        { ok: /[0-9]/.test(pw), l: '0-9' },
                        { ok: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pw), l: 'Special' },
                      ];
                      return <div className="flex gap-1.5 flex-wrap">{checks.map(c => (
                        <span key={c.l} className={`text-[9px] px-1 rounded ${c.ok ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-400'}`}>{c.ok ? '✓' : '✗'} {c.l}</span>
                      ))}</div>;
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
